/**
 * `<hero-split-slider>` — el pase de diapositivas del hero de la home, sobre Embla.
 *
 * SE DESPLAZA EN HORIZONTAL, como el resto de los carruseles del tema. Sin JavaScript
 * el CSS apila los slides en la misma celda de grid y cruza opacidad: el primer
 * slide se ve entero y los demás quedan ocultos, que es un estado completo.
 *
 * CON EL DEDO NO HAY EMBLA (`rielNativo()`): `hero.css` convierte la pista en un
 * scroller con scroll-snap y este módulo sigue el `scrollLeft` para `is-active`, los
 * puntos, las flechas y el autoplay (`scrollTo` sobre la pista). Si el puntero cambia
 * (DevTools, híbridos) se cambia de motor en caliente.
 *
 * Qué hace la librería:
 * - el desplazamiento entre slides, con arrastre 1:1 y sin resorte;
 * - el alto del marco, igual al del slide visible (`altoAdaptativo`);
 * - el bucle (`loop`, desde `data-loop`);
 * - el arrastre táctil. El autoplay y su pausa al pasar el puntero viven aquí:
 *   Embla no trae autoplay y no se añade el plugin.
 *
 * Qué se queda aquí:
 * - `is-active` en el slide y en el punto. `motion/hero-slider` busca
 *   `.hero-split.is-active [data-stagger]` para animar la entrada del texto.
 * - El evento `hero-slide:active`.
 * - Pausar los `<video>` de los slides que no tocan (y todos si
 *   `prefers-reduced-motion`): el autoplay del primero sale de Liquid.
 * - `data-hero-ready`, que avisa a quien espere a que el módulo haya cableado.
 *
 * Embla no reparenta las celdas, así que la entrada del titular no se cancela
 * al montar: no hace falta `#conservarEntrada()`.
 */

import { prefersReduced } from "motion/tokens";
import {
  cargarEmbla,
  opcionesDesde,
  montar,
  instante,
  enBucle,
  rielNativo,
  vigilarRielNativo,
} from "core/embla";

class HeroSplitSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLElement[]} */
  #dots = [];
  /** @type {HTMLElement | null} */
  #prev = null;
  /** @type {HTMLElement | null} */
  #next = null;
  /** @type {any} */
  #embla = null;
  #activeIndex = 0;
  #loop = false;
  #autoplayMs = 0;
  #hovering = false;
  /** @type {number} */
  #timer = 0;
  #desconectado = false;
  /**
   * Se renueva en cada conexión y se aborta al desconectar: un custom element puede
   * desconectarse y volver a conectarse —el carrito y la PLP reemplazan fragmentos
   * enteros—, y sin esto cada reconexión sumaba otro listener a los controles y dejaba
   * `#desconectado` en `true`, que impedía volver a montar Embla.
   * @type {AbortController | null}
   */
  #abort = null;
  /** Un `AbortController` por montaje de Embla: sus listeners de teclado no sobreviven al cambio de motor. */
  #montaje = null;
  /** @type {(() => void) | null} */
  #cancelarPuntero = null;
  /** El listener de `scroll` del modo nativo ya está puesto (una vez por conexión). */
  #nativoListo = false;
  #scrollRaf = 0;

  connectedCallback() {
    this.#desconectado = false;
    this.#abort?.abort();
    this.#abort = new AbortController();

    this.#track = this.querySelector(":scope > .hero-split-slider__track");
    this.#slides = this.#track
      ? [...this.#track.children].filter((el) => el instanceof HTMLElement)
      : [];
    this.#dots = [...this.querySelectorAll("[data-hero-dot]")].filter(
      (el) => el instanceof HTMLElement,
    );
    this.#prev = this.querySelector("[data-hero-prev]");
    this.#next = this.querySelector("[data-hero-next]");
    this.#loop = this.dataset.loop === "true";
    this.#autoplayMs = prefersReduced() ? 0 : Number(this.dataset.autoplay) || 0;

    const activo = this.#slides.findIndex((slide) => slide.classList.contains("is-active"));
    this.#activeIndex = activo >= 0 ? activo : 0;

    this.#announceActive();
    this.#syncVideos();

    if (!this.#track || this.#slides.length < 2) {
      this.dataset.heroReady = "";
      return;
    }

    // Los mandos se cablean UNA vez por conexión y despachan al motor que haya:
    // Embla o el scroller nativo. Así cambiar de motor no duplica listeners.
    const signal = this.#abort?.signal;
    this.#prev?.addEventListener("click", () => this.#step(-1), { signal });
    this.#next?.addEventListener("click", () => this.#step(1), { signal });
    this.#dots.forEach((dot, index) => {
      dot.addEventListener("click", () => this.#irA(index), { signal });
    });
    if (this.#autoplayMs > 0) {
      this.addEventListener("mouseenter", () => this.#setHovering(true), { signal });
      this.addEventListener("mouseleave", () => this.#setHovering(false), { signal });
    }
    this.#cancelarPuntero = vigilarRielNativo((nativo) => this.#cambiarMotor(nativo));

    if (rielNativo()) this.#activarNativo();
    else void this.#activar();
  }

  disconnectedCallback() {
    this.#desconectado = true;
    this.#abort?.abort();
    this.#cancelarPuntero?.();
    this.#pararAutoplay();
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = 0;
    this.#nativoListo = false;
    this.#syncVideos(true);
    this.#desmontar();
  }

  async #activar() {
    const EmblaCarousel = await cargarEmbla();
    // Entre el `import()` y aquí el puntero pudo pasar a ser un dedo.
    if (this.#desconectado || !this.#track || this.#embla || rielNativo()) return;

    this.#montaje = new AbortController();
    this.#embla = montar(EmblaCarousel, this, {
      host: this,
      container: this.#track,
      slides: ".hero-split",
      signal: this.#montaje.signal,
      wrapSiguiente: this.#loop,
      aislar: true,
      altoAdaptativo: true,
      opciones: {
        align: "start",
        loop: this.#loop,
        containScroll: this.#loop ? false : "trimSnaps",
        startIndex: this.#activeIndex,
        ...opcionesDesde(this),
      },
    });

    this.#embla.on("select", () => this.#alSeleccionar(this.#embla?.selectedScrollSnap() ?? 0));

    this.#listo();
  }

  /**
   * Motor nativo: la pista es un scroller CSS (`hero.css`, `pointer: coarse`). El
   * índice activo se lee del `scrollLeft` a razón de un ancho por slide; el resto
   * (puntos, flechas, vídeos, autoplay) es el mismo código que con Embla.
   */
  #activarNativo() {
    const track = this.#track;
    if (this.#desconectado || !track) return;

    if (this.#activeIndex > 0) {
      track.scrollTo({ left: this.#activeIndex * track.clientWidth, behavior: "instant" });
    }

    if (!this.#nativoListo) {
      this.#nativoListo = true;
      track.addEventListener(
        "scroll",
        () => {
          if (this.#embla || this.#scrollRaf) return;
          this.#scrollRaf = requestAnimationFrame(() => {
            this.#scrollRaf = 0;
            const w = track.clientWidth;
            if (w < 2) return;
            this.#alSeleccionar(Math.round(track.scrollLeft / w));
          });
        },
        { passive: true, signal: this.#abort?.signal },
      );
    }

    this.#listo();
  }

  #listo() {
    this.#reiniciarAutoplay();
    this.#syncEnds();
    this.#pintarPausa();
    this.dataset.heroReady = "";
  }

  /** @param {boolean} nativo */
  #cambiarMotor(nativo) {
    if (this.#desconectado) return;
    if (nativo) {
      this.#desmontar();
      this.#activarNativo();
      return;
    }
    void this.#activar();
  }

  /** Quita Embla; `montar()` retira `is-enhanced` y el CSS vuelve a mandar. */
  #desmontar() {
    this.#montaje?.abort();
    this.#montaje = null;
    this.#embla?.destroy();
    this.#embla = null;
  }

  /**
   * Ir a un slide con el motor que haya. En nativo el `behavior` lo decide el CSS
   * (`scroll-behavior`), que ya respeta `prefers-reduced-motion`.
   * @param {number} index
   */
  #irA(index) {
    if (this.#embla) {
      this.#embla.scrollTo(index, instante());
      return;
    }
    const track = this.#track;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth });
  }

  /** @param {boolean} hovering */
  #setHovering(hovering) {
    this.#hovering = hovering;
    this.#pintarPausa();
    if (hovering) this.#pararAutoplay();
    else this.#reiniciarAutoplay();
  }

  #pararAutoplay() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = 0;
    }
  }

  #reiniciarAutoplay() {
    this.#pararAutoplay();
    if (this.#autoplayMs <= 0 || this.#hovering || this.#desconectado) return;
    this.#timer = window.setInterval(() => this.#step(1), this.#autoplayMs);
  }

  /**
   * `is-paused` es solo estado: lo pone y lo quita esto. Hoy ninguna regla cuelga de
   * él; se mantiene porque es el único sitio donde se sabe si el pase corre.
   */
  #pintarPausa() {
    const corriendo = this.#autoplayMs > 0 && !this.#hovering;
    this.classList.toggle("is-paused", !corriendo);
  }

  /** @param {number} delta */
  #step(delta) {
    const api = this.#embla;
    if (!api) {
      const last = this.#slides.length - 1;
      let next = this.#activeIndex + delta;
      if (next > last) {
        if (!this.#loop) return;
        next = 0;
      } else if (next < 0) {
        if (!this.#loop) return;
        next = last;
      }
      this.#irA(next);
      return;
    }
    const jump = instante();
    const wrap = this.#loop || enBucle(api);
    if (delta > 0) {
      if (api.canScrollNext()) api.scrollNext(jump);
      else if (wrap) api.scrollTo(0, true);
    } else if (api.canScrollPrev()) {
      api.scrollPrev(jump);
    } else if (wrap) {
      api.scrollTo(api.scrollSnapList().length - 1, true);
    }
  }

  /** @param {number} index */
  #alSeleccionar(index) {
    if (index === this.#activeIndex || index < 0 || index >= this.#slides.length) return;

    this.#slides[this.#activeIndex]?.classList.remove("is-active");
    this.#dots[this.#activeIndex]?.classList.remove("is-active");
    this.#dots[this.#activeIndex]?.setAttribute("aria-selected", "false");
    this.#activeIndex = index;
    this.#slides[index]?.classList.add("is-active");
    this.#dots[index]?.classList.add("is-active");
    this.#dots[index]?.setAttribute("aria-selected", "true");

    this.#syncEnds();
    this.#announceActive();
    this.#syncVideos();
    this.#reiniciarAutoplay();
  }

  /**
   * El `<video>` del slide activo sigue; el resto se pausa para no descargar
   * seis bucles a la vez. Sin JS el primero ya trae `autoplay` en el HTML.
   * @param {boolean} [pausarTodo]
   */
  #syncVideos(pausarTodo = false) {
    const reducir = pausarTodo || prefersReduced();
    this.#slides.forEach((slide, index) => {
      slide.querySelectorAll("video").forEach((video) => {
        if (reducir || index !== this.#activeIndex) {
          video.pause();
        } else {
          const play = video.play();
          if (play) void play.catch(() => {});
        }
      });
    });
  }

  #syncEnds() {
    if (this.#loop) return;
    const last = this.#slides.length - 1;
    if (this.#prev instanceof HTMLButtonElement) this.#prev.disabled = this.#activeIndex === 0;
    if (this.#next instanceof HTMLButtonElement) this.#next.disabled = this.#activeIndex === last;
  }

  #announceActive() {
    this.dispatchEvent(
      new CustomEvent("hero-slide:active", {
        bubbles: true,
        detail: { activeIndex: this.#activeIndex },
      }),
    );
  }
}

if (!customElements.get("hero-split-slider")) {
  customElements.define("hero-split-slider", HeroSplitSlider);
}

export { HeroSplitSlider };
